import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FortnoxLogo,
  KustomLogo,
  LedyerLogo,
  VismaLogo,
  RentmanLogo,
  SitooLogo,
  WooCommerceLogo,
  WetailLogo
} from '@/components/logos';
import {
  faUser,
  faHandRock,
  faClock,
  faPlug,
  faPowerOff,
  faChevronDown,
  faChevronRight,
  faKey,
  faCogs,
  faBalanceScale,
  faCreditCard,
  faTag,
  faGlobe,
  faFileExport,
  faGift,
  faUsers,
  faBell,
  faFileInvoice,
  faBuilding,
  faBox
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/store/authStore';
import { MenuItem } from '@/types';

interface SidebarProps {
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems: MenuItem[] = [
    {
      title: 'Wetail Middle Layer',
      hiddenOnCollapse: true
    },
    {
      href: '/sites',
      title: 'Sites',
      icon: 'fa fa-user'
    },
    {
      href: '/products',
      title: 'Products',
      icon: 'fa fa-box'
    },
    {
      href: '/sync',
      title: 'Manual Sync',
      icon: 'fa fa-hand-rock'
    },
    {
      title: 'Cronjobs',
      icon: 'fa fa-clock',
      child: [
        {
          href: '/master_slave_integrations',
          title: 'Master Slave Integrations'
        },
        {
          href: '/synchronization_jobs',
          title: 'Synchronization Jobs'
        }
      ]
    },
    {
      title: 'External Systems',
      icon: 'fa fa-plug',
      child: [
        {
          title: 'Kustom',
          icon: 'kustom-logo',
          child: [
            {
              href: '/settings/kustom/authentication',
              title: 'Authentication'
            },
            {
              href: '/settings/kustom/integration',
              title: 'Latest synchronization timestamps'
            }
          ]
        },
        {
          title: 'Ledyer',
          icon: 'ledyer-logo',
          child: [
            {
              href: '/settings/ledyer/authentication',
              title: 'Authentication'
            },
            {
              href: '/settings/ledyer/integration',
              title: 'Latest synchronization timestamps'
            }
          ]
        },
        {
          title: 'Fortnox',
          icon: 'fortnox-logo',
          child: [
            {
              title: 'Settings',
              child: [
                {
                  href: '/settings/fortnox/authentication',
                  title: 'Access Token',
                  icon: 'fa fa-key'
                },
                {
                  href: '/settings/fortnox/integration',
                  title: 'Integration',
                  icon: 'fa fa-cogs'
                },
                {
                  href: '/settings/fortnox/reconciliation',
                  title: 'Reconciliation',
                  icon: 'fa fa-balance-scale'
                },
                {
                  href: '/settings/fortnox/invoice_payment_account',
                  title: 'Invoice Payment Account',
                  icon: 'fa fa-credit-card'
                },
                {
                  href: '/settings/fortnox/product_synchronization_settings',
                  title: 'Product synchronization settings',
                  icon: 'fa fa-tag'
                },
                {
                  href: '/settings/fortnox/oss',
                  title: 'OSS',
                  icon: 'fa fa-globe'
                },
                {
                  href: '/settings/fortnox/export_account',
                  title: 'Export Account',
                  icon: 'fa fa-file-export'
                },
                {
                  href: '/settings/fortnox/giftcard_account',
                  title: 'Giftcard Account',
                  icon: 'fa fa-gift'
                },
                {
                  href: '/settings/fortnox/customer_debt_accounts',
                  title: 'Customer Debt Accounts',
                  icon: 'fa fa-users'
                },
                {
                  href: '/settings/fortnox/order_listener',
                  title: 'Order Listener',
                  icon: 'fa fa-bell'
                },
                {
                  href: '/settings/fortnox/invoice_listener',
                  title: 'Invoice Listener',
                  icon: 'fa fa-file-invoice'
                }
              ]
            },
            {
              title: 'Data',
              child: [
                {
                  href: '/user_data/fortnox/processed_transactions',
                  title: 'Processed Transactions'
                },
                {
                  href: '/user_data/fortnox/processed_payouts',
                  title: 'Processed Payouts'
                }
              ]
            }
          ]
        },
        {
          title: 'Visma',
          icon: 'visma-logo',
          child: [
            {
              href: '/settings/visma/authentication',
              title: 'Authentication'
            },
            {
              href: '/settings/visma/customer_debt_accounts',
              title: 'Customer Debt Accounts'
            },
            {
              href: '/settings/visma/integration',
              title: 'Integration Settings'
            },
            {
              href: '/settings/visma/invoice_payment',
              title: 'Invoice Payment'
            },
            {
              href: '/settings/visma/invoice',
              title: 'Invoice Settings'
            },
            {
              title: 'Data',
              child: [
                {
                  href: '/user_data/visma/processed_transactions',
                  title: 'Processed Transactions'
                },
                {
                  href: '/user_data/visma/processed_payouts',
                  title: 'Processed Payouts'
                }
              ]
            }
          ]
        },
        {
          title: 'Sitoo',
          icon: 'sitoo-logo',
          child: [
              {
                href: '/settings/sitoo/authentication',
                title: 'Authentication'
              },
            {
              href: '/settings/sitoo/integration',
              title: 'Integration Settings'
            }
          ]
        },
        {
          title: 'Rentman',
          icon: 'rentman-logo',
          child: [
            {
              href: '/settings/rentman/authentication',
              title: 'Authentication'
            },
            {
              href: '/settings/rentman/integration',
              title: 'Latest synchronization timestamps'
            }
          ]
        },
        {
          title: 'Woocommerce',
          icon: 'woocommerce-logo',
          child: [
            {
              href: '/settings/woocommerce/authentication',
              title: 'Authentication'
            },
            {
              href: '/settings/woocommerce/integration_timestamps',
              title: 'Integration Timestamps'
            }
          ]
        },
        {
          title: 'Shopify',
          icon: 'woocommerce-logo',
          child: [
            {
              href: '/settings/shopify/authentication',
              title: 'Authentication'
            },
            {
              href: '/settings/shopify/integration_timestamps',
              title: 'Integration Timestamps'
            },
            {
              href: '/settings/shopify/webhooks',
              title: 'Webhooks'
            }
          ]
        },
        {
          href: '/settings/csv_source',
          title: 'CSV Source'
        }
      ]
    },
    {
      href: '#',
      id: 'logout',
      title: 'Logout',
      icon: 'fa fa-power-off'
    }
  ];

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const toggleExpanded = (itemTitle: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemTitle)) {
      newExpanded.delete(itemTitle);
    } else {
      newExpanded.add(itemTitle);
    }
    setExpandedItems(newExpanded);
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.id === 'logout') {
      logout();
      navigate('/login');
    } else if (item.href && item.href !== '#') {
      const href = typeof item.href === 'string' ? item.href : item.href.path;
      navigate(href);
    }
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.child && item.child.length > 0;
    const isExpanded = expandedItems.has(item.title);
    const isActive = location.pathname === item.href;
    const paddingLeft = level * 16 + 16;

    if (item.hiddenOnCollapse && collapsed) {
      return null;
    }

    return (
      <div key={item.title}>
        <div
          className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''} ${collapsed ? 'justify-center' : ''}`}
          style={{ paddingLeft: collapsed ? 0 : paddingLeft }}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.title);
            } else {
              handleItemClick(item);
            }
          }}
        >
          <div className="flex items-center space-x-3">
            {item.icon && (
              <>
                {item.icon === 'fortnox-logo' ? (
                  <FortnoxLogo size="sm" />
                ) : item.icon === 'klarna-logo' ? (
                  <KustomLogo size="sm" />
                ) : item.icon === 'ledyer-logo' ? (
                  <LedyerLogo size="sm" />
                ) : item.icon === 'visma-logo' ? (
                  <VismaLogo size="sm" />
                ) : item.icon === 'rentman-logo' ? (
                  <RentmanLogo size="sm" />
                ) : item.icon === 'sitoo-logo' ? (
                  <SitooLogo size="sm" />
                ) : item.icon === 'woocommerce-logo' ? (
                  <WooCommerceLogo size="sm" />
                ) : (
                  <FontAwesomeIcon
                    icon={
                      item.icon === 'fa fa-user' ? faUser :
                      item.icon === 'fa fa-box' ? faBox :
                      item.icon === 'fa fa-hand-rock' ? faHandRock :
                      item.icon === 'fa fa-clock' ? faClock :
                      item.icon === 'fa fa-plug' ? faPlug :
                      item.icon === 'fa fa-power-off' ? faPowerOff :
                      item.icon === 'fa fa-building' ? faBuilding :
                      item.icon === 'fa fa-key' ? faKey :
                      item.icon === 'fa fa-cogs' ? faCogs :
                      item.icon === 'fa fa-balance-scale' ? faBalanceScale :
                      item.icon === 'fa fa-credit-card' ? faCreditCard :
                      item.icon === 'fa fa-tag' ? faTag :
                      item.icon === 'fa fa-globe' ? faGlobe :
                      item.icon === 'fa fa-file-export' ? faFileExport :
                      item.icon === 'fa fa-gift' ? faGift :
                      item.icon === 'fa fa-users' ? faUsers :
                      item.icon === 'fa fa-bell' ? faBell :
                      item.icon === 'fa fa-file-invoice' ? faFileInvoice :
                      faUser
                    }
                    className="w-4 h-4"
                  />
                )}
              </>
            )}
            {!collapsed && (
              <span className="text-sm font-medium">{item.title}</span>
            )}
          </div>
          {hasChildren && !collapsed && (
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              className="w-3 h-3"
            />
          )}
        </div>

        {hasChildren && isExpanded && !collapsed && (
          <div className="ml-4">
            {item.child!.map(child => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`fixed left-0 top-0 h-full sidebar z-50 transition-all duration-300 ${
      collapsed ? 'w-16' : 'w-64'
    } ${className}`}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <button
            onClick={toggleCollapse}
            className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <WetailLogo size="md" />
          </button>
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto py-2">
          {menuItems.map(item => renderMenuItem(item))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
